import React from "react";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Grid,
  Divider,
  Avatar,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import {
  Star as StarIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import {
  getRiskColor,
  getRiskTextColor,
} from "../../../../Risks/RiskManagement/utils/riskUtils";
import {
  EmptyFields,
} from "../../../../../utils/enums";
import { styles } from "../../../../Risks/RiskManagement/styles/RiskManagement.styles";
import { formatDate } from "../../../../../utils/dateUtils";
import { TreatmentStatus } from "../../../../Risks/RiskManagement/utils/enums";

export default function AuditRiskOverviewTab({ risk }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 2,
        height: "auto",
        backgroundColor: theme.customColors.background.dialog,
      }}
    >
      <Grid container spacing={2}>
        {/* Description Card */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ mb: 1.5, color: theme.customColors.text.strong }}
              >
                Description
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.6,
                  color: theme.customColors.text.strong,
                  backgroundColor: theme.customColors.background.header,
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${theme.customColors.border.subtle}`,
                }}
              >
                {risk?.risk_description || EmptyFields.DESCRIPTION}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Other Details Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <InfoIcon
                  sx={{ color: theme.palette._blue.main, fontSize: 18 }}
                />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: theme.customColors.text.strong }}
                >
                  Other Details
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.text.secondary,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Category
                    </Typography>
                    <Chip
                      label={
                        risk?.risk_category_title || EmptyFields.RISK_CATEGORY
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        borderColor: theme.customColors.border.strong,
                        color: theme.customColors.text.strong,
                        mt: 0.5,
                        alignSelf: "flex-start",
                        height: "20px",
                      }}
                    />
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.text.secondary,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Owner
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: theme.customColors.text.strong }}
                    >
                      {risk?.risk_owner_email || EmptyFields.OWNER}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.text.secondary,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <ShieldIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Treatment Status
                    </Typography>
                    <Chip
                      label={
                        risk?.treatment_title || TreatmentStatus.ACCEPTED
                      }
                      {...(risk?.treatment_title === TreatmentStatus.ACCEPTED
                        ? styles.AcceptedTreatmentStatusChip
                        : styles.TreatedTreatmentStatusChip)}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Assessment Card */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <TrendingUpIcon
                  sx={{ color: theme.palette._blue.main, fontSize: 18 }}
                />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: theme.customColors.text.strong }}
                >
                  Risk Assessment
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Inherent Risk - Read Only */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.customColors.background.dialog,
                      borderRadius: 2,
                      border: `1px solid ${theme.customColors.border.subtle}`,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: theme.customColors.text.strong }}
                      >
                        Inherent Risk Score
                      </Typography>
                      <Tooltip title="Based on original assessment" arrow>
                        <StarIcon
                          sx={{
                            color: theme.customColors.text.secondary,
                            fontSize: 12,
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        backgroundColor: getRiskColor(
                          risk?.inherent_risk_score
                        ),
                        borderRadius: 2,
                        border: `2px solid ${getRiskTextColor(
                          risk?.inherent_risk_score
                        )}20`,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h2"
                        fontWeight={800}
                        sx={{
                          color: getRiskTextColor(risk?.inherent_risk_score),
                          textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                      >
                        {risk?.inherent_risk_score}
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "center",
                        display: "block",
                        color: theme.customColors.text.secondary,
                        fontWeight: 500,
                        fontSize: "10px",
                      }}
                    >
                      Impact: {risk?.risk_inherent_impact} × Likelihood:{" "}
                      {risk?.risk_inherent_likelihood}
                    </Typography>
                  </Box>
                </Grid>

                {/* Residual Risk - Read Only for Audit View */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.customColors.background.dialog,
                      borderRadius: 2,
                      border: `1px solid ${theme.customColors.border.subtle}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            fontSize: "12px",
                            color: theme.customColors.text.strong,
                          }}
                        >
                          Residual Risk Score
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        backgroundColor: getRiskColor(
                          risk?.residual_risk_score
                        ),
                        borderRadius: 2,
                        border: `2px solid ${getRiskTextColor(
                          risk?.residual_risk_score
                        )}20`,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h2"
                        fontWeight={800}
                        sx={{
                          color: getRiskTextColor(risk?.residual_risk_score),
                          textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                      >
                        {risk?.residual_risk_score}
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "center",
                        display: "block",
                        color: theme.customColors.text.secondary,
                        fontWeight: 500,
                        fontSize: "10px",
                      }}
                    >
                      Last Updated: {formatDate(risk?.updated_at)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
